// ============================================================
// statDrains - the stat-drain family (v8.290.0).
//
// Sapping/Withering/Slowing/Numbing/Cowing/Nullifying drain a
// characteristic on hit. THE DRAIN IS A CONDITION: `Dn:` codes
// beside the Pen: family, so Firestore sync, echo prevention,
// condition chips and the round tick all come free - no parallel
// ledger to rot.
//
// Semantics (ADR, amended v8.293.0): amounts SUM, the duration
// refreshes to DRAIN_DURATION on every fresh hit; Nullifying is
// scene-long (no duration - cleared manually or by the GM). At
// CONSUMPTION, X comes off the stat AS THE SHEET SHOWS IT, 1:1 -
// dodge/parry skills lose X points, TB/SB/WP lose X off the BONUS,
// psy loses X rating. The original floor(X/10) exchange rate for
// bonuses read as canon but played as nothing: Withering(5) drained
// zero, which the first playtest called a bug - correctly. Floors
// hold at read time, never at write time.
// ============================================================
import type { Target } from '../types';

export type DrainChar = 'str' | 'tgh' | 'agi' | 'wsbs' | 'wp' | 'psy';

export const DRAIN_DURATION = 3;

/** Rule id -> drained characteristic. One table, six rules. */
export const DRAIN_TABLE: { rule: string; char: DrainChar; label: string }[] = [
  { rule: 'Sapping', char: 'str', label: 'Strength' },
  { rule: 'Withering', char: 'tgh', label: 'Toughness' },
  { rule: 'Slowing', char: 'agi', label: 'Agility' },
  { rule: 'Numbing', char: 'wsbs', label: 'WS & BS' },
  { rule: 'Cowing', char: 'wp', label: 'Willpower' },
  { rule: 'Nullifying', char: 'psy', label: 'Psy Rating' },
];

/**
 * THE UNITS EACH DRAIN IS TYPED IN, in one table (v8.296.0) - the chip,
 * the combat-log note, the rule's own description and the test modifier
 * all read it, so they cannot drift into three different scales.
 *
 *   - Toughness, Strength and Willpower are drained in BONUS points, the
 *     way the target sheet shows them: Withering(2) is 2 off the soak,
 *     and a bonus point is ten characteristic points, so tests move 20.
 *   - Agility and WS/BS are drained in the full characteristic points
 *     those stats are shown in: Slowing(15) is 15, on the sheet and on
 *     the dodge alike.
 *   - A psy RATING is neither - it has no test at all.
 */
export const DRAIN_UNITS: Record<DrainChar, { testPerPoint: number; unit: string }> = {
  str: { testPerPoint: 10, unit: 'bonus' },
  tgh: { testPerPoint: 10, unit: 'bonus' },
  wp: { testPerPoint: 10, unit: 'bonus' },
  agi: { testPerPoint: 1, unit: '' },
  wsbs: { testPerPoint: 1, unit: '' },
  psy: { testPerPoint: 0, unit: '' },
};

/** "Toughness bonus" / "Agility" - the label a chip or note leads with. */
export function drainUnitLabel(char: string): string {
  const unit = DRAIN_UNITS[char as DrainChar]?.unit;
  return unit ? `${drainLabel(char)} ${unit}` : drainLabel(char);
}

function drainLabel(char: string): string {
  return DRAIN_TABLE.find((d) => d.char === char)?.label || char;
}

export function buildDrainCode(char: DrainChar, amount: number, rounds = 0): string {
  return rounds > 0 ? `Dn:${char}=${amount}:${rounds}` : `Dn:${char}=${amount}`;
}

export function parseDrainCode(code: string): { char: DrainChar; amount: number; rounds: number } | null {
  const m = code.match(/^Dn:(\w+)=(\d+)(?::(\d+))?$/);
  if (!m) return null;
  return { char: m[1] as DrainChar, amount: parseInt(m[2], 10), rounds: m[3] ? parseInt(m[3], 10) : 0 };
}

/**
 * Merge a fresh drain into a condition list IN PLACE (the weapon-effect
 * ctx.newConds contract): one code per characteristic, amounts summed,
 * duration refreshed.
 */
export function mergeDrain(conds: string[], char: DrainChar, amount: number, rounds = 0): void {
  for (let i = 0; i < conds.length; i++) {
    const parsed = parseDrainCode(conds[i]);
    if (parsed && parsed.char === char) {
      conds[i] = buildDrainCode(char, parsed.amount + amount, rounds);
      return;
    }
  }
  conds.push(buildDrainCode(char, amount, rounds));
}

/** Per-characteristic drain totals carried by a condition list. */
export function readDrains(conds: string[] | undefined): Record<DrainChar, number> {
  const totals: Record<DrainChar, number> = { str: 0, tgh: 0, agi: 0, wsbs: 0, wp: 0, psy: 0 };
  for (const c of conds || []) {
    const parsed = parseDrainCode(c);
    if (parsed && parsed.char in totals) totals[parsed.char] += parsed.amount;
  }
  return totals;
}

/**
 * The consumption overlay: a NEW Target with the drains applied that
 * `conditionModifiers` cannot express. Returns the SAME reference when
 * nothing applies, so identity-keyed projection caches stay warm.
 *
 * NARROWED IN v8.294.0 TO PSY ALONE. Every other drained stat is a TEST
 * base - dodge Agility, parry WS, Toughness, Strength, Willpower - and
 * tests already have one mechanism that reaches every row the app
 * renders (the character sheet's, the plain target's), the defence
 * chain and the drained combatant's own attack roll:
 * `getConditionTestModifier`. Subtracting here as well would apply one
 * drain twice on any row that reads a projected base. Psy rating is not
 * a characteristic and has no test, so it stays.
 *
 * The one place a drain must still bite raw arithmetic is soak, and
 * `calcSingleHitDamage` reads the conditions itself - see `soakDrain`.
 */
export function applyDrainOverlay(target: Target): Target {
  const d = readDrains(target.conditions);
  if (!d.psy) return target;
  return { ...target, psyRating: Math.max(0, (target.psyRating || 0) - d.psy) };
}

/**
 * Toughness drained, for the SOAK - the one consumer that is arithmetic
 * rather than a test, so no condition modifier can reach it. Withering
 * is typed in bonus points, which is exactly what the soak is paid in,
 * so this is the number as given (v8.296.0).
 */
export function soakDrain(conditions: string[] | undefined): number {
  return readDrains(conditions).tgh;
}
